import { EXIT_DELAY_IN_MILLISECONDS } from '1_components/Transitition/settings';
import useAudioRoutingStore from 'state/audioRoutingStore';
import useClientSideRouting from 'state/clientSideRoutingStore';
import { useCloudOverlayStore } from 'state/cloudOverlayStore';
import { useCustomCameraStore } from 'state/customCameraStore';
import { useDisableStore } from 'state/disableStore';
import { useGlitchTransitionStore } from 'state/glitchTransitionStore';
import { useHeaderStore } from 'state/headerStore';
import { useResponseStore } from 'state/responseStore';
import { useAudioStore } from 'state/sfx/audioStore';
import { useMusicStore } from 'state/sfx/musicStore';
import { PERIL_STAGE, SKY_STATE, useThreeStore } from 'state/threeStore';
import useTilesStore from 'state/tilesStore';
import { useVoiceAssistantStore } from 'state/voiceAssistantStore';
import { getPrioritizedRisks } from 'utils/experience-script/data-mappings/getPrioritizedRisks';

export const shouldRedirectToHome = (pathName: string) => {
  const exemptPaths = ['/', '/landing'];
  return !exemptPaths.includes(pathName);
};

const SKY_FOR_PERIL: Record<PERIL_STAGE, SKY_STATE> = {
  storm: 'STORMY',
  cyclone: 'CLOUDY',
  flood: 'FLOOD',
  bushfire: 'BUSHFIRE',
  HIDDEN: 'TRANSPARENT',
};

export const adjustRouteSettings = (pathName: string) => {
  const { setHeaderSettings } = useHeaderStore.getState();
  const { play: playGlitchTransition } = useGlitchTransitionStore.getState();

  const {
    setCanvasOverLayout,
    setEarth,
    setTiles,
    setBuilding,
    setSky,
    setPeril,
    setWeather,
    setIndicationRings,
    earth: { stage: earthStage },
    tiles: { stage: tilesStage },
  } = useThreeStore.getState();

  const setCamera = useCustomCameraStore.getState().setCustomCameraVariant;

  const { setVisible: setCloudsOverlayVisible } =
    useCloudOverlayStore.getState();

  const stopAudioSound = useAudioStore.getState().fadeOut;
  const { stopAll: stopAllMusic, fadeOut: fadeOutMusic } =
    useMusicStore.getState();

  const { response } = useResponseStore.getState();

  const perils = getPrioritizedRisks(response.risks);

  switch (pathName) {
    case 'weather':
      setWeather({
        isRendered: true,
        stage: useResponseStore.getState().response.weather?.precisCode,
      });
      setSky('TRANSPARENT');
      setHeaderSettings({
        isHeaderVisible: true,
        isBackButtonVisible: false,
        isLogoVisible: false,
      });
      setEarth({ isRendered: false });
      setTiles({ isRendered: false });
      setCanvasOverLayout(false);

      setBuilding({ isRendered: false, stage: 'HIDDEN' });
      setCloudsOverlayVisible(false);
      setCamera('WEATHER');
      break;

    case 'building_1':
      playGlitchTransition();
      setSky('BUILDING');

      setBuilding({ isRendered: true, stage: 'VISIBLE' });
      setHeaderSettings({
        isHeaderVisible: true,
        isBackButtonVisible: false,
        isLogoVisible: false,
        isReportButtonVisible: true,
      });
      setEarth({ isRendered: false });
      setTiles({ isRendered: false });
      setCloudsOverlayVisible(false);
      break;

    case 'building_2':
      setSky('BUILDING');

      setBuilding({ isRendered: true, stage: 'VISIBLE' });
      setHeaderSettings({
        isHeaderVisible: true,
        isBackButtonVisible: false,
        isLogoVisible: false,
        isReportButtonVisible: true,
      });
      setEarth({ isRendered: false });
      setTiles({ isRendered: false });
      setCloudsOverlayVisible(false);
      break;

    case 'building_3':
      setSky('BUILDING');

      setBuilding({ isRendered: true, stage: 'VISIBLE' });
      setHeaderSettings({
        isHeaderVisible: true,
        isBackButtonVisible: false,
        isReportButtonVisible: true,
      });
      setEarth({ isRendered: false });
      setTiles({ isRendered: false });
      setCamera('TILES');
      setCloudsOverlayVisible(false);
      break;

    case 'peril_1':
      setWeather({ isRendered: false });
      setPeril({ isRendered: true, stage: perils[0].peril as PERIL_STAGE });
      setBuilding({ isRendered: false, stage: 'HIDDEN' });

      setHeaderSettings({
        isHeaderVisible: true,
        isBackButtonVisible: false,
        isReportButtonVisible: true,
      });
      setSky(SKY_FOR_PERIL[perils[0].peril as PERIL_STAGE]);
      setTiles({ isRendered: true, stage: 'VISIBLE' });
      setCamera(perils[0].peril);
      if (earthStage !== 'DESTINATION_TRANSITION_END') {
        setEarth({ stage: 'DESTINATION_TRANSITION_START' });
      }
      setCanvasOverLayout(false);
      setIndicationRings({ isRendered: false });
      break;

    case 'peril_2':
      setWeather({ isRendered: false });
      setPeril({ isRendered: true, stage: perils[1].peril as PERIL_STAGE });
      setBuilding({ isRendered: false, stage: 'HIDDEN' });

      setHeaderSettings({
        isHeaderVisible: true,
        isBackButtonVisible: false,
        isReportButtonVisible: true,
      });
      setSky(SKY_FOR_PERIL[perils[1].peril as PERIL_STAGE]);
      setTiles({ isRendered: true, stage: 'VISIBLE' });
      setCamera(perils[1].peril);
      if (earthStage !== 'DESTINATION_TRANSITION_END') {
        setEarth({ stage: 'DESTINATION_TRANSITION_START' });
      }
      setCanvasOverLayout(false);
      setIndicationRings({ isRendered: false });
      break;

    case 'reels':
      setSky('TRANSPARENT');
      setPeril({ isRendered: false });
      setBuilding({ isRendered: false, stage: 'HIDDEN' });
      setWeather({ isRendered: false });

      setHeaderSettings({
        isHeaderVisible: true,
        isBackButtonVisible: false,
        isLogoVisible: false,
      });
      setTiles({ isRendered: false });
      setEarth({ isRendered: false });
      setCanvasOverLayout(true);
      break;

    case '':
      setSky('NIGHT');
      setPeril({ isRendered: false });
      setBuilding({ isRendered: false, stage: 'HIDDEN' });
      setWeather({ isRendered: false });

      setHeaderSettings({ isHeaderVisible: false });
      setCanvasOverLayout(false);
      setEarth({ stage: 'HIDDEN', isRendered: true });
      sessionStorage.setItem('nextRoute', '');
      break;

    case 'landing':
      setSky('NIGHT');
      setPeril({ isRendered: false });
      setBuilding({ isRendered: false, stage: 'HIDDEN' });
      setWeather({ isRendered: false });

      setHeaderSettings({
        isLogoVisible: true,
        isHeaderVisible: true,
        isBackButtonVisible: false,
        isReportButtonVisible: false,
      });
      setCanvasOverLayout(false);
      setEarth({ stage: 'LANDING', isRendered: true });
      sessionStorage.setItem('nextRoute', '');
      useTilesStore.getState().setAttribution([]);
      useTilesStore.getState().setPlace(null);
      useTilesStore.getState().setTiles([]);
      useAudioRoutingStore.getState().setAudioChunks([]);
      useAudioRoutingStore.getState().setCurrentStep(0);
      useAudioRoutingStore.getState().setIsPlaying(false);
      useClientSideRouting.getState().setPath('experience-preload');
      break;

    case 'address-not-supported':
      setSky('NIGHT');
      setBuilding({ isRendered: false, stage: 'HIDDEN' });
      setWeather({ isRendered: false });

      setHeaderSettings({
        isLogoVisible: true,
        isHeaderVisible: true,
        isBackButtonVisible: false,
        isReportButtonVisible: false,
      });
      setCanvasOverLayout(false);
      setEarth({ stage: 'LANDING', isRendered: true });
      sessionStorage.setItem('nextRoute', '');
      break;

    case 'technical-error':
      setSky('NIGHT');
      setBuilding({ isRendered: false, stage: 'HIDDEN' });
      setWeather({ isRendered: false });

      setHeaderSettings({
        isLogoVisible: true,
        isHeaderVisible: true,
        isBackButtonVisible: false,
        isReportButtonVisible: false,
      });
      setCanvasOverLayout(false);
      setEarth({ stage: 'LANDING', isRendered: true });
      sessionStorage.setItem('nextRoute', '');
      break;

    case 'experience-preload':
      setSky('NIGHT');
      setBuilding({ isRendered: false, stage: 'HIDDEN' });
      setWeather({ isRendered: false });

      setHeaderSettings({
        isHeaderVisible: false,
        isBackButtonVisible: false,
        isLogoVisible: false,
        isReportButtonVisible: false,
        isInfoMenuButtonCompact: true,
      });
      setEarth({ stage: 'CENTERED', isRendered: true });
      setTiles({ stage: 'HIDDEN', isRendered: false });
      setCanvasOverLayout(false);
      sessionStorage.setItem('nextRoute', '');

      useAudioRoutingStore.getState().setCurrentStep(0);
      useAudioRoutingStore.getState().setIsPlaying(false);
      break;

    case 'script-debug-ui':
      setSky('TRANSPARENT');
      setBuilding({ isRendered: false, stage: 'HIDDEN' });
      setWeather({ isRendered: false });

      setHeaderSettings({
        isHeaderVisible: false,
        isLogoVisible: false,
        isReportButtonVisible: false,
      });
      setCanvasOverLayout(false);
      break;

    case 'tiles':
      setPeril({ isRendered: false });
      setBuilding({ isRendered: false, stage: 'HIDDEN' });
      setWeather({ isRendered: false });

      setHeaderSettings({
        isHeaderVisible: true,
        isBackButtonVisible: false,
        isLogoVisible: false,
      });
      setTimeout(
        () => setHeaderSettings({ isReportButtonVisible: true }),
        7000
      );
      fadeOutMusic('landingAudio');
      setCamera('TILES');

      if (tilesStage !== 'HIDDEN') {
        setTiles({ isRendered: true });
        setSky('DAY');
      }

      setCanvasOverLayout(false);
      break;

    case 'outro':
      setBuilding({ isRendered: false, stage: 'HIDDEN' });

      setHeaderSettings({
        isHeaderVisible: true,
        isBackButtonVisible: false,
        isReportButtonVisible: true,
      });
      setSky('DAY');
      setCamera('SPACE');
      setCanvasOverLayout(false);
      break;

    case 'script':
      setSky('TRANSPARENT');
      setBuilding({ isRendered: false, stage: 'HIDDEN' });
      setWeather({ isRendered: false });

      setHeaderSettings({ isHeaderVisible: false });
      break;

    case 'timeline-screen':
      setSky('TRANSPARENT');
      setBuilding({ isRendered: false, stage: 'HIDDEN' });
      setWeather({ isRendered: false });

      setHeaderSettings({
        isHeaderVisible: true,
        isBackButtonVisible: false,
        isLogoVisible: false,
      });
      setTiles({ isRendered: false });
      setEarth({ isRendered: false });
      setCloudsOverlayVisible(false);
      stopAudioSound('atmos');
      break;

    case 'age-brackets':
      setSky('DAY');
      setPeril({ isRendered: false });
      setBuilding({ isRendered: false, stage: 'HIDDEN' });
      setWeather({ isRendered: false });
      setHeaderSettings({
        isHeaderVisible: true,
        isBackButtonVisible: false,
        isLogoVisible: false,
      });
      setEarth({ isRendered: false });
      setTiles({ isRendered: true });
      setCamera('TILES');
      setCanvasOverLayout(false);
      setIndicationRings({ stage: 'HIDDEN' });
      break;

    case 'thank-you':
      setHeaderSettings({
        isHeaderVisible: true,
        isLogoVisible: true,
        isInfoMenuButtonCompact: true,
        isReportButtonVisible: false,
        isBackButtonVisible: false,
      });
      setCloudsOverlayVisible(false);
      stopAllMusic();
      stopAudioSound('atmos');
      useDisableStore.getState().setIsExperienceDisabled(true);
      useVoiceAssistantStore
        .getState()
        .setVoiceAssistantSettings({ isVoiceAssistantVisible: false });
      break;

    case 'report':
      stopAllMusic();
      stopAudioSound('atmos');
      setHeaderSettings({
        isHeaderVisible: true,
        isBackButtonVisible: true,
        isLogoVisible: true,
        isReportButtonVisible: false,
        isInfoMenuButtonCompact: true,
        backButtonDestination: '/thank-you',
      });
      setCloudsOverlayVisible(false);
      useDisableStore.getState().setIsExperienceDisabled(true);
      useVoiceAssistantStore
        .getState()
        .setVoiceAssistantSettings({ isVoiceAssistantVisible: false });
      break;

    case 'report/peril':
      stopAllMusic();
      stopAudioSound('atmos');
      setHeaderSettings({
        isHeaderVisible: true,
        isBackButtonVisible: true,
        isLogoVisible: true,
        isReportButtonVisible: false,
        isInfoMenuButtonCompact: true,
        backButtonDestination: '/report',
      });
      setCloudsOverlayVisible(false);
      useDisableStore.getState().setIsExperienceDisabled(true);
      useVoiceAssistantStore
        .getState()
        .setVoiceAssistantSettings({ isVoiceAssistantVisible: false });
      break;

    case 'contractors':
      useThreeStore.getState().setIndicationRings({ isRendered: false });
      adjustRouteSettings('tiles');
      break;

    case 'about-the-project':
      setSky('TRANSPARENT');
      setPeril({ isRendered: false });
      setBuilding({ isRendered: false, stage: 'HIDDEN' });
      setWeather({ isRendered: false });
      setCloudsOverlayVisible(false);

      setHeaderSettings({
        isHeaderVisible: false,
      });
  }

  const weatherVariant = [pathName, sessionStorage.getItem('nextRoute') || '']
    .find(route => route.includes('weather/'))
    ?.replace('weather/', '');

  setTimeout(() => {
    setWeather({ stage: weatherVariant });
  }, EXIT_DELAY_IN_MILLISECONDS);
};
